import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCircle2, Clock, Copy, Loader2, Mail } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DESTINATARIO_LABELS = {
  familiare_principale: "Familiare principale",
  tutore: "Tutore",
  altro: "Altro indirizzo",
};

export default function StoricoInviiDialog({ open, onOpenChange, modulo, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const copyLink = (invio) => {
    if (!invio.portal_link) return;
    navigator.clipboard.writeText(invio.portal_link).then(() => {
      setCopiedId(invio.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const fetchInvii = useCallback(async () => {
    if (!modulo || !token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/moduli-firma/${modulo.id}/invii`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.items || []);
    } catch {
      toast.error("Errore caricamento storico invii");
    } finally {
      setLoading(false);
    }
  }, [modulo, token]);

  useEffect(() => { if (open) fetchInvii(); }, [open, fetchInvii]);

  const formatDateTime = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

  if (!modulo) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Storico invii — {modulo.titolo}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#00A86B]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-slate-400">
            <Mail className="h-8 w-8 text-slate-300" />
            Nessun invio effettuato per questo modulo.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((i) => {
              const firmato = i.stato === "firmato";
              return (
                <div key={i.id} className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{i.ospite_nome || "Ospite"}</p>
                    <p className="truncate text-xs text-slate-500">
                      {DESTINATARIO_LABELS[i.destinatario_tipo] || i.destinatario_tipo} · {i.email}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-400">Inviato il {formatDateTime(i.created_at)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {firmato ? (
                      <Badge className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" />
                        Firmato {formatDateTime(i.firmato_at)}
                      </Badge>
                    ) : (
                      <>
                        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700">
                          <Clock className="h-3 w-3" />
                          In attesa
                        </Badge>
                        {i.portal_link && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Copia link portale" onClick={() => copyLink(i)}>
                            {copiedId === i.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
